import { GridCellProps } from '@progress/kendo-react-grid';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { GridColumn } from 'src/components/GridWrapper';
import useAuth from 'src/hooks/useAuth';
import useWindowSize from 'src/hooks/useWindowSize';
import ContactNameCell from 'src/sections/work_area/table_cell/ContactNameCell';
import SearchResultsActionCell from 'src/sections/work_area/table_cell/SearchResultsActionCell';
import CompanyCell from 'src/sections/work_area/table_cell/WorkAreaCompanyCell';
import { CompanySearchResult } from 'src/types/work_area';

const useWorkAreaSearchColumns = (onCloseModal?: () => void) => {
  const { t } = useTranslation();
  const { width } = useWindowSize();
  const { isSuperAdmin } = useAuth();

  const gridWidth = useMemo(() => Math.round(width * 0.9 - 4), [width]);

  const setPercentage = useMemo(
    () => (percentage: number) => Math.round((gridWidth / 100) * percentage),
    [gridWidth]
  );

  const columns: GridColumn<CompanySearchResult>[] = useMemo(
    () => [
      {
        field: 'contactName',
        title: t('userNameAndRole'),
        dataType: 'string',
        width: setPercentage(24),
        menu: false,
        component: ContactNameCell,
      },
      {
        field: 'managementCompanyName',
        title: t('managementCompany'),
        dataType: 'string',
        width: setPercentage(18),
        menu: false,
        component: CompanyCell,
      },
      {
        field: 'parentCompanyName',
        title: t('company'),
        dataType: 'string',
        width: setPercentage(18),
        menu: false,
        component: CompanyCell,
      },
      {
        field: 'contactEmail',
        title: t('emailAddress'),
        dataType: 'string',
        width: setPercentage(20),
        menu: false,
      },
      ...(isSuperAdmin
        ? [
            {
              field: 'loginName',
              title: t('userName'),
              dataType: 'string' as const,
              width: setPercentage(20),
              menu: false,
            },
          ]
        : []),
      {
        field: 'poSpendingLimit',
        title: t('poSpendingLimit'),
        width: setPercentage(10),
        dataType: 'dollar',
        className: 'right-justify',
        headerClassName: 'right-justify',
        menu: false,
      },
      {
        field: 'action',
        title: t('Action'),
        width: Math.max(setPercentage(10), 100),
        sortable: false,
        component: (props: GridCellProps) => (
          <SearchResultsActionCell {...props} onCloseModal={onCloseModal} />
        ),
        menu: false,
      },
    ],
    [onCloseModal, setPercentage, t, isSuperAdmin]
  );

  return columns;
};

export default useWorkAreaSearchColumns;
